import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import config from "../../config";
import CircularProgress from "@mui/material/CircularProgress";
import HighlightOffOutlinedIcon from "@mui/icons-material/HighlightOffOutlined";
import axios from "axios";
import Popup from "./Popup";

function AddProductPopup({ onClose }) {
    const navigate = useNavigate();
    const [product, setProduct] = useState("");
    const [productPrice, setProductPrice] = useState("");
    const [sku, setSKU] = useState("");
    const [invent, setInvent] = useState("");
    const [showPopup, setShowPopup] = useState(false);
    const [popupMessage, setPopupMessage] = useState("");
    const [description, setDescription] = useState("");
    const [unit, setUnit] = useState("");
    const [packSize, setPackSize] = useState("");
    const [loading, setLoading] = useState(false);
    const [category, setCategory] = useState();
    const [categories, setCategories] = useState([]);
    const [expiryDate, setExpiryDate] = useState("");
    const branchName = localStorage.getItem("branch_name");
    const sname = localStorage.getItem("s-name");
    const bid = localStorage.getItem("branch_id");

    useEffect(() => {
        const token = localStorage.getItem("token");

        axios
            .get(
                `${config.apiUrl}/api/swalook/product_category/?branch_name=${bid}`,
                {
                    headers: {
                        Authorization: `Token ${token}`,
                        "Content-Type": "application/json",
                    },
                },
            )
            .then((response) => {
                if (response.data.status) {
                    setCategories(response.data.data);
                } else {
                    setPopupMessage("Failed to fetch categories.");
                    setShowPopup(true);
                }
            })
            .catch((err) => {
                console.error("Error fetching categories:", err);
                setPopupMessage("Failed to fetch categories.");
                setShowPopup(true);
            });
    }, [bid]);

    const formatDate = (dateString) => {
        let parts;

        if (dateString.includes("/")) {
            parts = dateString.split("/");
        } else if (dateString.includes("-")) {
            parts = dateString.split("-");
        } else {
            return "Invalid Date";
        }

        if (parts.length !== 3) return "Invalid Date";

        let [part1, part2, year] = parts.map((num) => parseInt(num, 10));

        if (part1 > 12) {
            return `${year}-${String(part2).padStart(2, "0")}-${String(
                part1,
            ).padStart(2, "0")}`;
        } else {
            return `${year}-${String(part1).padStart(2, "0")}-${String(
                part2,
            ).padStart(2, "0")}`;
        }
    };

    const expiryDates = formatDate(expiryDate);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        const token = localStorage.getItem("token");
        const bid = localStorage.getItem("branch_id");

        if (!category) {
            setPopupMessage("Please select a category.");
            setShowPopup(true);
            return;
        }

        const payload = {
            product_name: product,
            product_price: parseFloat(productPrice),
            product_description: description || "",
            product_id: sku || "",
            stocks_in_hand: parseInt(invent, 10),
            pack_size: parseFloat(packSize),
            unit: unit,
            category: category,
            expiry_date: expiryDate,
        };

        try {
            const response = await fetch(
                `${config.apiUrl}/api/swalook/inventory/product/?branch_name=${bid}`,
                {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Token ${token}`,
                    },
                    body: JSON.stringify(payload),
                },
            );

            const result = await response.json();

            if (response.ok) {
                setPopupMessage("Product added successfully!");
                onClose();
                // window.location.reload();
            } else {
                setPopupMessage(result.message || "Failed to add product.");
                console.error("API Error:", result);
            }
        } catch (error) {
            console.error("Network Error:", error);
            setPopupMessage("An error occurred. Please try again.");
        } finally {
            setLoading(false);
            setShowPopup(true);
        }
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm z-50 flex justify-center items-center">
            <div className="bg-white rounded-lg shadow-lg w-full max-w-md mx-4 p-6 relative">
                <div className="flex justify-between items-center mb-4">
                    <h3 className="text-xl font-semibold text-gray-800">
                        Add Product
                    </h3>
                    <button
                        onClick={onClose}
                        className="text-red-500 hover:text-red-700"
                    >
                        <HighlightOffOutlinedIcon />
                    </button>
                </div>

                <hr className="border-t-2 border-blue-900 my-4" />

                <form onSubmit={handleSubmit} className="space-y-4">
                    <div className="flex items-center">
                        <label className="w-32 text-sm font-medium text-gray-700">
                            Name:
                        </label>
                        <input
                            type="text"
                            className="flex-1 h-10 px-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            placeholder="Product Name"
                            required
                            value={product}
                            onChange={(e) => setProduct(e.target.value)}
                        />
                    </div>

                    <div className="flex items-center">
                        <label className="w-32 text-sm font-medium text-gray-700">
                            Category:
                        </label>
                        <select
                            className="flex-1 h-10 px-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-700"
                            value={category || ""}
                            onChange={(e) => setCategory(e.target.value)}
                            required
                        >
                            <option value="" disabled>
                                Select a Category
                            </option>
                            {categories.map((cat) => (
                                <option key={cat.id} value={cat.id}>
                                    {cat.product_category}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div className="flex items-center">
                        <label className="w-32 text-sm font-medium text-gray-700">
                            SKU:
                        </label>
                        <input
                            type="text"
                            className="flex-1 h-10 px-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            placeholder="Id of product"
                            required
                            value={sku}
                            onChange={(e) => setSKU(e.target.value)}
                        />
                    </div>

                    <div className="flex items-center">
                        <label className="w-32 text-sm font-medium text-gray-700">
                            Price:
                        </label>
                        <input
                            type="number"
                            className="flex-1 h-10 px-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            placeholder="Price"
                            required
                            value={productPrice}
                            onChange={(e) => setProductPrice(e.target.value)}
                        />
                    </div>

                    <div className="flex items-center">
                        <label className="w-32 text-sm font-medium text-gray-700">
                            Quantity:
                        </label>
                        <input
                            type="number"
                            className="flex-1 h-10 px-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            placeholder="Quantity"
                            required
                            value={invent}
                            onChange={(e) => setInvent(e.target.value)}
                        />
                    </div>
                    <div className="flex items-center">
                        <label className="w-32 text-sm font-medium text-gray-700">
                            Pack Size:
                        </label>
                        <input
                            type="number"
                            className="flex-1 h-10 px-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            placeholder="Quantity per bottle (e.g. 1000)"
                            required
                            value={packSize}
                            onChange={(e) => setPackSize(e.target.value)}
                        />
                    </div>
                    <div className="flex items-center">
                        <label className="w-32 text-sm font-medium text-gray-700">
                            Unit:
                        </label>
                        <select
                            className="flex-1 h-10 px-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-700"
                            value={unit}
                            onChange={(e) => setUnit(e.target.value)}
                        >
                            <option value="">Select Unit</option>
                            <option value="ml">ml</option>
                            <option value="gm">gm</option>
                        </select>
                    </div>

                    <div className="flex items-center">
                        <label className="w-32 text-sm font-medium text-gray-700">
                            Expiry Date:
                        </label>
                        <input
                            type="date"
                            className="flex-1 h-10 px-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-700"
                            required
                            value={expiryDate}
                            onChange={(e) => setExpiryDate(e.target.value)}
                        />
                    </div>

                    <div className="flex items-center">
                        <label className="w-32 text-sm font-medium text-gray-700">
                            Description:
                        </label>
                        <input
                            type="text"
                            className="flex-1 h-10 px-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            placeholder="Description"
                            required
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                        />
                    </div>

                    <div className="flex justify-center pt-4">
                        <button
                            type="submit"
                            className="bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-6 rounded-full transition duration-300 flex items-center"
                            disabled={loading}
                        >
                            {loading ? (
                                <>
                                    <CircularProgress
                                        size={20}
                                        className="text-white mr-2"
                                    />
                                    Processing...
                                </>
                            ) : (
                                "Save"
                            )}
                        </button>
                    </div>
                </form>
            </div>

            {showPopup && (
                <Popup
                    message={popupMessage}
                    onClose={() => {
                        setShowPopup(false);
                        navigate(`/${sname}/${branchName}/inventory`);
                    }}
                />
            )}
        </div>
    );
}

export default AddProductPopup;
